'use client'
import { useTranslations } from 'next-intl'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { axiosApiData } from '@/lib/api-client'
import { api } from '@/lib/api'
import { useDropzone } from 'react-dropzone'
import { useVoiceRecording } from '../../hooks/useVoiceRecording'
import {
  useUnifiedWebSocketConnection,
  useUnifiedWebSocketMessage,
} from '@/lib/websocket-unified'
import { useWebSocket } from '@/lib/websocket'
import {
  useFlowExecution,
  type FlowMessagePayload,
} from '@/hooks/useFlowExecution'
import { useFlowOverride } from '@/hooks/useFlowOverride'
import { DEFAULT_SITE_ID } from '@/app/api/base'
import { collectClientSessionMetadata } from '@/lib/chat/session-metadata'
import { useAuthStore } from '@/stores/auth'
import FlowDebugger from './FlowDebugger'
import type { FlowAttachment } from '@/types/flow'

const getFlowErrorDisplayMessage = (rawError?: string) => {
  if (!rawError) {
    return 'We hit a snag processing your request. Please try again.'
  }

  const normalized = rawError.toLowerCase()
  if (
    normalized.includes('no matching condition') ||
    normalized.includes('route by interest')
  ) {
    return "I couldn't match that request to any of the available interests. Please pick one of the menu options or rephrase your question."
  }

  return 'We hit a snag processing your request. Please try again.'
}

const parseMenuOptionsFromContent = (content?: string) => {
  if (typeof content !== 'string' || content.trim().length === 0) {
    return null
  }

  // Menu nodes store a transcript-friendly list like "- [1] Label" in history.
  const lines = content.split('\n')
  const optionRegex = /^-\s*\[(\d+)\]\s+(.+)$/
  const options: { id: string; label: string; value: string }[] = []

  let idx = lines.length - 1
  while (idx >= 0) {
    const line = lines[idx].trim()
    const match = line.match(optionRegex)
    if (!match) {
      break
    }
    const label = match[2].trim()
    if (label) {
      options.unshift({ id: match[1], label, value: label })
    }
    idx -= 1
  }

  if (options.length === 0) {
    return null
  }

  const trimmedLines = lines.slice(0, idx + 1)
  while (
    trimmedLines.length > 0 &&
    trimmedLines[trimmedLines.length - 1].trim() === ''
  ) {
    trimmedLines.pop()
  }

  return {
    content: trimmedLines.join('\n'),
    options,
  }
}

export interface TextResult {
  text: string
  type: 'vector' | 'graph' | string
  document_id?: string | null
  score: number
  wordCount: number
  metadata?: {
    title?: string
    url?: string
    source?: string
    chunk_index?: number
    document_type?: string
    [key: string]: any
  }
}

export interface Message {
  id: string
  role: 'user' | 'assistant' | 'agent'
  content: string
  timestamp: Date
  imageUrl?: string
  imageS3Key?: string
  attachments?: FlowAttachment[]
  paymentRequest?: {
    paymentUrl?: string
    buttonText?: string
    amount?: number | string
    currency?: string
    description?: string
  }
  agentName?: string
  sources?: TextResult[]
  audioUrl?: string
  fileUrl?: string
  fileName?: string
  menuOptions?: {
    id: string
    label: string
    value: string
    url?: string
  }[]
  menuOptionsDisabled?: boolean
  formNode?: {
    nodeId: string
    config: {
      message: string
      fields: {
        id: string
        label: string
        inputType:
          | 'text'
          | 'phone'
          | 'email'
          | 'textarea'
          | 'number'
          | 'password'
          | 'toggle'
          | 'select'
          | 'checkbox'
        variableName: string
        placeholder?: string
        required?: boolean
      }[]
      formName?: string
      submitButtonText?: string
    }
  }
}

export interface IntegrationStatus {
  whatsappEnabled: boolean
  phoneNumber: string | null
  facebookEnabled: boolean
  pageId: string | null
  telegramEnabled: boolean
  telegramUsername: string | null
}

export interface WebSocketMessage {
  type: string
  data: {
    agentName?: string
    messageId?: string
    content?: string
    timestamp?: number
    imageUrl?: string
    audioUrl?: string
    fileUrl?: string
    fileName?: string
    sessionId?: string
    reason?: string
    [key: string]: any
  }
}

export interface ChatCoreProps {
  botSlug?: string
  onSessionCreated?: (sessionId: string) => void
  onMessageSent?: (message: Message) => void
  onMessageReceived?: (message: Message) => void
  onHandoverRequested?: () => void
  onHandoverCompleted?: () => void
  onHandoverAssigned?: () => void
  onOpenChat?: () => void // Callback to auto-open chat when entry node has openChat enabled
  className?: string
  children: (props: ChatCoreRenderProps) => React.ReactNode
}

type FlowHandoverConfig = {
  teamId?: number | null
  teamName?: string | null
  specificAgentId?: number
  priority?: 'low' | 'normal' | 'high' | 'urgent'
  message?: string
  sendToAllAgents?: boolean
  autoAssign?: boolean
}

export interface ChatCoreRenderProps {
  // State
  messages: Message[]
  inputText: string
  isLoading: boolean
  sessionId: string | null
  uploadedImage: {
    url: string
    file: File
    isUploading?: boolean
    uploadError?: string
  } | null
  integrationStatus: IntegrationStatus
  isHandedOver: boolean
  agentName: string | null
  showHandoverButton: boolean
  hasNewMessage: boolean
  wsConnected: boolean
  wsConnectionIssue: boolean
  wsConnectionMessage: string | null
  wsReconnecting: boolean
  retryWebSocket: () => void

  // Flow state
  isFlowActive: boolean
  flowSession: any | null
  isProcessingFlow: boolean
  flowError: string | null
  hasDefaultFlow: boolean | null
  defaultBot: {
    id: number
    name: string
    slug: string
    status?: number
  } | null

  // Voice recording
  isRecording: boolean
  isProcessingVoice: boolean
  voiceError: string | null
  isVoiceSupported: boolean

  // Drag and drop
  isDragActive: boolean
  getRootProps: () => any
  getInputProps: () => any

  // Actions
  setInputText: (text: string) => void
  sendMessage: (
    text?: string,
    imageUrl?: string,
    opts?: { flowInput?: string }
  ) => Promise<void>
  clearUploadedImage: () => void
  requestHandover: (config?: FlowHandoverConfig) => Promise<void>
  toggleRecording: () => void
  clearVoiceError: () => void
  resetFlow: () => Promise<void>
  clearFlowError: () => void
  onDrop: (acceptedFiles: File[]) => Promise<void>
  // Execute a flow step (exposed for form/menu follow-ups)
  executeFlow: (userInput?: string, nodeId?: string) => Promise<any>
  // Refs for custom scroll behavior
  messagesContainerRef: React.RefObject<HTMLDivElement>
  inputRef: React.RefObject<HTMLInputElement>

  // Utilities
  t: (key: string) => string
}

// Helper function to generate unique IDs
let idCounter = 0
const generateUniqueId = (): string => {
  idCounter++
  return `${Date.now()}-${idCounter}-${Math.random().toString(36).substr(2, 9)}-${performance.now()}`
}

// Helper function to strip query parameters from URL for database storage
// Presigned URLs are too long (>255 chars) and temporary, so we store base URL only
const stripUrlQueryParams = (url: string | undefined): string | undefined => {
  if (!url) return undefined
  try {
    const urlObj = new URL(url)
    return `${urlObj.origin}${urlObj.pathname}`
  } catch {
    // If URL parsing fails, try simple string split
    const queryIndex = url.indexOf('?')
    return queryIndex > 0 ? url.substring(0, queryIndex) : url
  }
}

const normalizeAttachments = (
  attachments?: Array<any>,
  fallbackUrl?: string,
  baseId?: string,
  fallbackS3Key?: string
): FlowAttachment[] | undefined => {
  const normalized: FlowAttachment[] = Array.isArray(attachments)
    ? ((
        attachments.map((attachment, index) => {
          if (!attachment) {
            return null
          }

          const resolvedUrl =
            (typeof attachment.presignedUrl === 'string' &&
            attachment.presignedUrl.trim().length > 0
              ? attachment.presignedUrl.trim()
              : undefined) ||
            (typeof attachment.url === 'string' &&
            attachment.url.trim().length > 0
              ? attachment.url.trim()
              : undefined)

          const hasS3Key =
            typeof attachment.s3Key === 'string' &&
            attachment.s3Key.trim().length > 0

          if (!resolvedUrl && !hasS3Key) {
            return null
          }

          return {
            id:
              typeof attachment.id === 'string' && attachment.id
                ? attachment.id
                : `${baseId || 'attachment'}-${index}`,
            type: 'image',
            url: resolvedUrl,
            presignedUrl:
              typeof attachment.presignedUrl === 'string' &&
              attachment.presignedUrl.trim().length > 0
                ? attachment.presignedUrl.trim()
                : undefined,
            s3Key: hasS3Key ? attachment.s3Key!.trim() : undefined,
            name: typeof attachment.name === 'string' ? attachment.name : null,
            caption:
              typeof attachment.caption === 'string'
                ? attachment.caption
                : null,
            width:
              typeof attachment.width === 'number' ? attachment.width : null,
            height:
              typeof attachment.height === 'number' ? attachment.height : null,
          }
        }) as Array<FlowAttachment | null>
      ).filter(attachment => attachment !== null) as FlowAttachment[])
    : []

  if (normalized.length === 0 && (fallbackUrl || fallbackS3Key)) {
    normalized.push({
      id: `${baseId || 'attachment'}-fallback`,
      type: 'image',
      url: fallbackUrl,
      s3Key:
        typeof fallbackS3Key === 'string' && fallbackS3Key.trim().length > 0
          ? fallbackS3Key.trim()
          : undefined,
    })
  }

  return normalized.length > 0 ? normalized : undefined
}

const BOT_STORAGE_KEY_PREFIX = 'chat_assigned_bot'
export function ChatCore({
  botSlug,
  onSessionCreated,
  onMessageSent,
  onMessageReceived,
  onHandoverRequested,
  onHandoverCompleted,
  onHandoverAssigned,
  onOpenChat,
  className,
  children,
}: ChatCoreProps) {
  const t = useTranslations('chat')
  const { isUserAuthenticated, publicUser } = useAuthStore()
  const [messages, setMessages] = useState<Message[]>([])
  const [inputText, setInputText] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [assignedBot, setAssignedBot] = useState<{
    id: number
    transferType: 'full' | 'context'
  } | null>(null)
  const [activeFlowBotState, setActiveFlowBotState] = useState<{
    nodeId: string
    botId: number
  } | null>(null)
  const [uploadedImage, setUploadedImage] = useState<{
    url: string
    file: File
    isUploading?: boolean
    uploadError?: string
    s3Key?: string
    transcription?: string
  } | null>(null)
  const [integrationStatus, setIntegrationStatus] = useState<IntegrationStatus>(
    {
      whatsappEnabled: false,
      phoneNumber: null,
      facebookEnabled: false,
      pageId: null,
      telegramEnabled: false,
      telegramUsername: null,
    }
  )
  const [isHandedOver, setIsHandedOver] = useState(false)
  const [agentName, setAgentName] = useState<string | null>(null)
  const [showHandoverButton, setShowHandoverButton] = useState(true)
  const [sessionStatusChecked, setSessionStatusChecked] = useState(false)
  const [hasNewMessage, setHasNewMessage] = useState(false)
  const [wsRegistrationTimeoutReached, setWsRegistrationTimeoutReached] =
    useState(false)
  const [isRealtimeReady, setIsRealtimeReady] = useState(false)
  const { flowId: flowIdOverride, flowSlug: flowSlugOverride } =
    useFlowOverride()

  const botAssignmentWaitRef = useRef<(() => void) | null>(null)
  const assignedBotRef = useRef(assignedBot)
  const activeFlowBotStateRef = useRef(activeFlowBotState)

  useEffect(() => {
    assignedBotRef.current = assignedBot
  }, [assignedBot])

  useEffect(() => {
    activeFlowBotStateRef.current = activeFlowBotState
  }, [activeFlowBotState])

  const siteApiBase = useMemo(() => {
    if (!DEFAULT_SITE_ID) {
      throw new Error(
        'Site ID is not defined and DEFAULT_SITE_ID is not set. Cannot perform chat API requests.'
      )
    }
    return '/api/be'
  }, [])

  const messagesContainerRef = useRef<HTMLDivElement>(null!)
  const inputRef = useRef<HTMLInputElement>(null!)
  const wsRegistrationTimerRef = useRef<number | null>(null)
  const wsRegistrationRetryIntervalRef = useRef<number | null>(null)
  const lastWsSessionIdRef = useRef<string | null>(null)

  // Message deduplication to prevent duplicate WebSocket messages
  const processedMessageIds = useRef(new Set<string>())

  // Track if we've loaded history for the current session to prevent duplicate loads
  const historyLoadedForSession = useRef<string | null>(null)

  // Clean up old message IDs periodically to prevent memory leaks
  useEffect(() => {
    const cleanup = setInterval(() => {
      processedMessageIds.current.clear()
    }, 60000) // Clear every minute

    return () => clearInterval(cleanup)
  }, [])

  // Dedup guard for assistant flow messages (handles batched/double-calls)
  const lastFlowAssistantRef = useRef<{ key: string; ts: number } | null>(null)

  const resolvePendingBotAssignment = useCallback(() => {
    const resolver = botAssignmentWaitRef.current
    if (resolver) {
      botAssignmentWaitRef.current = null
      resolver()
    }
  }, [])

  const waitForBotAssignment = useCallback(async () => {
    if (assignedBotRef.current?.id || activeFlowBotStateRef.current) {
      return
    }

    await new Promise<void>(resolve => {
      let settled = false
      let timeoutId: ReturnType<typeof setTimeout> | null = null

      const resolver = () => {
        if (settled) {
          return
        }
        settled = true
        if (timeoutId) {
          clearTimeout(timeoutId)
        }
        if (botAssignmentWaitRef.current === resolver) {
          botAssignmentWaitRef.current = null
        }
        resolve()
      }

      timeoutId = setTimeout(() => {
        if (settled) {
          return
        }
        settled = true
        if (botAssignmentWaitRef.current === resolver) {
          botAssignmentWaitRef.current = null
        }
        console.warn(
          '⏱️ [CHAT] Timed out waiting for bot assignment; continuing anyway'
        )
        resolve()
      }, 2000)

      botAssignmentWaitRef.current = resolver
    })
  }, [])

  useEffect(() => {
    return () => {
      const resolver = botAssignmentWaitRef.current
      if (resolver) {
        botAssignmentWaitRef.current = null
        resolver()
      }
    }
  }, [])

  // Auto-scroll is handled by layout components (FloatingChatLayout, PageChatLayout)

  const persistAssignedBot = useCallback(
    (
      bot: { id: number; transferType: 'full' | 'context' } | null,
      targetSessionId?: string | null
    ) => {
      if (typeof window === 'undefined') return
      const storageSessionId = targetSessionId ?? sessionId
      if (!storageSessionId) return
      const storageKey = `${BOT_STORAGE_KEY_PREFIX}:${storageSessionId}`
      if (bot) {
        localStorage.setItem(storageKey, JSON.stringify(bot))
      } else {
        localStorage.removeItem(storageKey)
      }
    },
    [sessionId]
  )

  // Voice recording hook
  const {
    isRecording,
    isProcessing: isProcessingVoice,
    error: voiceError,
    toggleRecording,
    clearError: clearVoiceError,
    isSupported: isVoiceSupported,
  } = useVoiceRecording({
    onAudioRecorded: audioData => {
      // Set the uploaded audio file (similar to image upload)
      setUploadedImage({
        url: audioData.url,
        file: audioData.file,
        s3Key: audioData.s3Key,
        isUploading: false,
        transcription: audioData.transcription, // Store transcription with uploaded file
      })
      // If there's transcription, set it as input text as well
      if (audioData.transcription) {
        setInputText(audioData.transcription)
      }
    },
  })

  // Flow execution hook

  const {
    isFlowActive,
    flowSession,
    isProcessingFlow,
    flowError,
    hasDefaultFlow,
    defaultBot,
    executeFlow,
    resetFlow,
    initializeFlow: initFlow,
    clearError: clearFlowError,
  } = useFlowExecution({
    sessionId,
    isHandedOver,
    sessionStatusChecked,
    isUserRegistered: isRealtimeReady,
    startFlowId: flowIdOverride,
    startFlowSlug: flowSlugOverride,
    onBotAssignment: async (
      botId,
      transferType,
      teamId,
      requiresBotResponse
    ) => {
      console.log('🤖 [CHAT CORE] Flow assigned bot:', botId, transferType, {
        teamId,
        requiresBotResponse,
      })
      if (!botId) {
        assignedBotRef.current = null
        resolvePendingBotAssignment()
        return
      }
      const assignment = { id: botId, transferType }
      assignedBotRef.current = assignment
      setAssignedBot(assignment)
      persistAssignedBot(assignment, sessionId)
      resolvePendingBotAssignment()

      // If requiresBotResponse is true, automatically trigger bot greeting
      if (requiresBotResponse) {
        console.log(
          '🤖 [CHAT] requiresBotResponse=true - Triggering bot response'
        )
        try {
          // Send a simple greeting to trigger bot response
          // The user's original message is already in chat history for context
          const responseData = await axiosApiData(`/api/chat/message`, {
            method: 'POST',
            data: {
              sessionId,
              message: 'hi',
              model: 'gpt-4o-mini',
              botId: botId,
              metadata: {},
            },
          })

          console.log(
            '✅ [CHAT] Bot greeting status:',
            responseData?.success !== false ? 'success' : 'failed'
          )
          if (responseData?.success === false || responseData?.error) {
            console.error('❌ [CHAT] Bot greeting error:', {
              success: responseData?.success,
              error: responseData?.error,
              message: responseData?.message,
            })
          }

          // Add bot response to UI
          if (responseData?.message) {
            const assistantMessageId = generateUniqueId()
            const assistantMessage: Message = {
              id: assistantMessageId,
              role: 'assistant',
              content: responseData.message,
              timestamp: new Date(),
              sources: responseData?.sources || undefined,
              attachments: normalizeAttachments(
                responseData?.attachments,
                responseData?.imageUrl,
                assistantMessageId,
                responseData?.imageS3Key
              ),
            }

            setMessages(prev => {
              const last = prev[prev.length - 1]
              const trimmedContent = (assistantMessage.content || '').trim()
              if (
                last &&
                last.role === 'assistant' &&
                (last.content || '').trim() === trimmedContent
              ) {
                console.log('🛑 [CHAT] Skipping duplicate bot greeting')
                return prev
              }
              console.log('🎯 [CHAT] Added bot greeting:', assistantMessage)
              return [...prev, assistantMessage]
            })
          }
        } catch (error) {
          console.error('❌ [CHAT] Failed to trigger bot greeting:', error)
        }
      }
    },
    onAgentHandover: handoverConfig => {
      assignedBotRef.current = null
      resolvePendingBotAssignment()
      requestHandover(handoverConfig)
    },
    onFlowComplete: () => {
      // Handle flow completion
      console.log('Flow completed')
    },
    onFlowMessage: useCallback(
      (payload: FlowMessagePayload) => {
        const {
          message: incomingMessage,
          attachments,
          paymentRequest,
          type,
          content,
          formNode,
        } = payload
        const messageText = incomingMessage ?? content ?? ''
        console.log('🎯 [CHAT CORE] onFlowMessage called with:', {
          type,
          message: messageText,
          attachments,
          formNode: formNode ? 'present' : 'absent',
        })

        // Handle form nodes specifically
        if (type === 'form' && formNode) {
          console.log('📋 [CHAT CORE] Rendering form node from flow:', formNode)
          const messageId = generateUniqueId()
          const normalizedAttachments =
            normalizeAttachments(attachments, undefined, messageId) ?? []

          const formMessage: Message = {
            id: messageId,
            role: 'assistant',
            content: messageText,
            timestamp: new Date(),
            ...(normalizedAttachments.length > 0
              ? { attachments: normalizedAttachments }
              : {}),
            formNode,
            ...(paymentRequest ? { paymentRequest } : {}),
          }

          setMessages(prev => [...prev, formMessage])
          return
        }

        // Regular message handling
        const messageId = generateUniqueId()
        const normalizedAttachments =
          normalizeAttachments(attachments, undefined, messageId) ?? []

        const trimmedNow = messageText.trim()
        const attachmentKey = normalizedAttachments
          .map(attachment => attachment.url || attachment.s3Key)
          .filter(Boolean)
          .join('|')
        const dedupeKeyBase = [trimmedNow, attachmentKey]
          .filter(Boolean)
          .join('::')
        const dedupeKey = dedupeKeyBase || '__empty__'
        const nowTs = Date.now()
        const last = lastFlowAssistantRef.current
        if (last && last.key === dedupeKey && nowTs - last.ts < 4000) {
          console.log(
            '🛑 [CHAT CORE] Early-skip duplicate assistant flow message:',
            dedupeKey
          )
          return
        }
        lastFlowAssistantRef.current = { key: dedupeKey, ts: nowTs }

        // Handle flow messages (e.g., from entry nodes)
        const flowMessage: Message = {
          id: messageId,
          role: 'assistant',
          content: messageText,
          timestamp: new Date(),
          ...(normalizedAttachments.length > 0
            ? {
                attachments: normalizedAttachments,
                imageUrl:
                  normalizedAttachments.length === 1
                    ? normalizedAttachments[0].url
                    : undefined,
                imageS3Key:
                  normalizedAttachments.length === 1
                    ? normalizedAttachments[0].s3Key || undefined
                    : undefined,
              }
            : {}),
          ...(paymentRequest ? { paymentRequest } : {}),
        }

        let messageAdded = false
        let nextMessagesSnapshot: Message[] | null = null

        setMessages(prev => {
          const updated = [...prev, flowMessage]
          nextMessagesSnapshot = updated
          messageAdded = true
          return updated
        })

        if (messageAdded && nextMessagesSnapshot) {
          console.log(
            '🎯 [CHAT CORE] Adding flow message to chat:',
            flowMessage
          )
          console.log(
            '🎯 [CHAT CORE] Updated messages array:',
            nextMessagesSnapshot
          )
          // Notify outside of setState to avoid duplicate side effects in StrictMode
          onMessageReceived?.(flowMessage)
        }
      },
      [onMessageReceived]
    ),
    onMenuOptions: useCallback(
      (
        messageText: string,
        options: { id: string; label: string; value: string }[]
      ) => {
        console.log('🎯 [CHAT CORE] onMenuOptions received:', {
          messageText,
          count: options?.length ?? 0,
          options,
        })
        // Attach menu options to the most recent assistant message (same content) once it exists
        setTimeout(() => {
          setMessages(prev => {
            const updated = [...prev]

            // Find the newest assistant message with matching content first
            let targetIdx = -1
            for (let i = updated.length - 1; i >= 0; i--) {
              const m = updated[i]
              if (m.role === 'assistant') {
                const sameContent =
                  (m.content || '').trim() === (messageText || '').trim()
                if (sameContent) {
                  targetIdx = i
                  break
                }
              }
            }

            // If not found by exact content, fallback to the last assistant message
            if (targetIdx === -1) {
              for (let i = updated.length - 1; i >= 0; i--) {
                if (updated[i].role === 'assistant') {
                  targetIdx = i
                  break
                }
              }
            }

            if (targetIdx !== -1) {
              const m = updated[targetIdx]
              console.log(
                '🎯 [CHAT CORE] Attaching menu options to assistant idx (by match/fallback):',
                targetIdx
              )
              updated[targetIdx] = {
                ...m,
                menuOptions: options,
                menuOptionsDisabled: false,
              }
              return updated
            }

            // Fallback: create a combined message with options
            console.log(
              '🧩 [CHAT CORE] Creating combined assistant message with options (no target found)'
            )
            return [
              ...updated,
              {
                id: generateUniqueId(),
                role: 'assistant',
                content: messageText || '',
                timestamp: new Date(),
                menuOptions: options,
                menuOptionsDisabled: false,
              },
            ]
          })
        }, 0)
      },
      []
    ),
    onOpenChat: onOpenChat, // Pass through the auto-open chat callback
  })

  useEffect(() => {
    if (typeof window === 'undefined' || !sessionId || assignedBot?.id) {
      return
    }

    const storageKey = `${BOT_STORAGE_KEY_PREFIX}:${sessionId}`
    try {
      const storedValue = localStorage.getItem(storageKey)
      if (storedValue) {
        const parsed = JSON.parse(storedValue)
        if (parsed?.id) {
          assignedBotRef.current = parsed
          setAssignedBot(parsed)
          return
        }
      }
    } catch (error) {
      console.error('Failed to load stored bot assignment:', error)
    }

    if (defaultBot?.id) {
      const fallback = { id: defaultBot.id, transferType: 'full' as const }
      assignedBotRef.current = fallback
      setAssignedBot(fallback)
      persistAssignedBot(fallback, sessionId)
    }
  }, [assignedBot, sessionId, defaultBot, persistAssignedBot])

  useEffect(() => {
    if (!sessionId) {
      return
    }
    persistAssignedBot(assignedBot, sessionId)
  }, [assignedBot, persistAssignedBot, sessionId])

  // Reduced logging

  const loadChatHistory = useCallback(
    async (sessionId: string, forceReload = false) => {
      try {
        // Skip if we've already loaded history for this session (unless forced)
        if (!forceReload && historyLoadedForSession.current === sessionId) {
          console.log(
            '⏭️ Skipping chat history load - already loaded for session:',
            sessionId
          )
          return
        }

        console.log('Loading chat history for session:', sessionId)

        // First, get the session status to check if it's handed over
        try {
          const statusResponse = await api.chat.getSessionStatus(sessionId)

          if (statusResponse.success && statusResponse.data) {
            const { isHandedOver, agentName } = statusResponse.data

            // Restore handover state
            setIsHandedOver(Boolean(isHandedOver))
            setAgentName(agentName || null)
            setShowHandoverButton(!isHandedOver)
          }
        } catch (statusError) {
          console.error('Failed to load session status:', statusError)
        } finally {
          // Mark session status as checked
          setSessionStatusChecked(true)
        }

        // Then try to get the chat messages
        try {
          console.log('🔍 Fetching messages for session:', sessionId)
          const response = await api.chat.getSessionMessages(sessionId)

          if (response.success && Array.isArray(response.data)) {
            // Filter out system messages for cleaner UI
            const userMessages = response.data.filter(
              (msg: any) => msg.role !== 'system'
            )

            console.log(
              '📋 Filtered messages (excluding system):',
              userMessages
            )

            const historyMessages = userMessages.map((msg: any) => ({
              id: msg.id || generateUniqueId(),
              role: msg.role,
              ...(() => {
                const parsedMenu = parseMenuOptionsFromContent(msg.content)
                return {
                  content: parsedMenu?.content ?? msg.content,
                  menuOptions: parsedMenu?.options,
                  menuOptionsDisabled: parsedMenu?.options ? true : undefined,
                }
              })(),
              timestamp: new Date(msg.timestamp || Date.now()),
              imageUrl: msg.imageUrl,
              imageS3Key: msg.imageS3Key,
              attachments: normalizeAttachments(
                msg.attachments,
                msg.imageUrl,
                msg.id || 'history',
                msg.imageS3Key
              ),
              audioUrl: msg.audioUrl,
              fileUrl: msg.fileUrl,
              fileName: msg.fileName,
              agentName:
                msg.agentFirstName && msg.agentLastName
                  ? `${msg.agentFirstName} ${msg.agentLastName}`.trim()
                  : msg.agentName || msg.username || 'Agent',
            }))

            if (historyMessages.length > 0) {
              const lastIndex = historyMessages.length - 1
              const lastMessage = historyMessages[lastIndex]
              if (
                lastMessage?.role === 'assistant' &&
                lastMessage.menuOptions &&
                lastMessage.menuOptions.length > 0
              ) {
                historyMessages[lastIndex] = {
                  ...lastMessage,
                  menuOptionsDisabled: false,
                }
              }
            }

            console.log(
              '🔄 Setting messages from history:',
              historyMessages.length
            )

            // Preserve any recent messages that were added locally while history was loading
            setMessages(prev => {
              const now = Date.now()
              const isRecent = (ts: any) => now - new Date(ts).getTime() < 7000

              // Preserve recent assistant, user, and agent messages created locally
              // system, event are hidden
              const rolesToPreserve = new Set(['assistant', 'user', 'agent'])
              const recent = prev.filter(
                m => rolesToPreserve.has(m.role) && isRecent(m.timestamp)
              )

              if (recent.length > 0) {
                console.log(
                  '🔄 Preserving recent local messages:',
                  recent.length
                )
                // Merge while de-duplicating by role+trimmed content
                const seen = new Set(
                  historyMessages.map(
                    (m: any) =>
                      `${m.role}|${m.id || ''}|${(m.content || '').trim()}`
                  )
                )
                const dedupRecent = recent.filter(m => {
                  const key = `${m.role}|${m.id || ''}|${(m.content || '').trim()}`
                  if (seen.has(key)) return false
                  seen.add(key)
                  return true
                })
                return [...historyMessages, ...dedupRecent]
              }

              return historyMessages
            })
            console.log(
              '✅ Loaded chat history:',
              historyMessages.length,
              'messages'
            )
            // Mark this session as having loaded history
            historyLoadedForSession.current = sessionId
          } else {
            console.log('⚠️ No messages found or invalid response structure')
            console.log('❌ Clearing messages (no valid response)')
            setMessages([])
            // Still mark as loaded to prevent retries
            historyLoadedForSession.current = sessionId
          }
        } catch (messagesError) {
          console.error('Failed to load messages:', messagesError)
          console.log('❌ Clearing messages (messages error)')
          setMessages([])
        }
      } catch (error) {
        console.error('Failed to load chat history:', error)
        console.log('❌ Clearing messages (general error)')
        setMessages([])
      }
    },
    [setMessages, setIsHandedOver, setAgentName, setShowHandoverButton]
  )

  // Initialize session
  const initializeSession = useCallback(async () => {
    try {
      // Check if we have an existing session in localStorage
      const existingSessionId = localStorage.getItem('chat_session_id')
      const sessionTimestamp = localStorage.getItem('chat_session_timestamp')

      // Check if session is still valid (within 3 hours)
      if (existingSessionId && sessionTimestamp) {
        const sessionAge = Date.now() - parseInt(sessionTimestamp)
        const threeHours = 3 * 60 * 60 * 1000 // 3 hours in milliseconds

        if (sessionAge < threeHours) {
          console.log('Reusing existing session:', existingSessionId)
          setSessionId(existingSessionId)
          onSessionCreated?.(existingSessionId)
          // Load chat history for existing session
          await loadChatHistory(existingSessionId)
          return existingSessionId
        } else {
          // Session expired, clear it
          localStorage.removeItem('chat_session_id')
          localStorage.removeItem('chat_session_timestamp')
        }
      }

      const sessionUrl = `${siteApiBase}/chat/session`
      const body: any = {}
      const metadata = collectClientSessionMetadata()
      if (Object.keys(metadata).length > 0) {
        body.metadata = metadata
      }

      // Include userId if public user is authenticated
      if (isUserAuthenticated && publicUser?.id) {
        body.userId = publicUser.id
      }

      if (botSlug) {
        // First get bot info
        const botResponse = await fetch(`${siteApiBase}/bots/slug/${botSlug}`)
        if (botResponse.ok) {
          const botData = await botResponse.json()
          body.botId = botData.id
        }
      }

      const response = await fetch(sessionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      })

      if (response.ok) {
        const data = await response.json()
        const newSessionId = data.sessionId
        setSessionId(newSessionId)
        onSessionCreated?.(newSessionId)

        // Store session in localStorage
        localStorage.setItem('chat_session_id', newSessionId)
        localStorage.setItem('chat_session_timestamp', Date.now().toString())

        console.log('Created new session:', newSessionId)

        // Try to load any existing messages for this session (in case it already exists in DB)
        try {
          await loadChatHistory(newSessionId)
        } catch (historyError) {
          console.log(
            'No existing history for new session, starting fresh:',
            historyError
          )
          // For new sessions, mark status as checked (no handover)
          setSessionStatusChecked(true)
        }

        return newSessionId
      }
      console.error(
        'Failed to create session:',
        response.status,
        response.statusText
      )
      // Mark session status as checked even on failure
      setSessionStatusChecked(true)
      return null
    } catch (error) {
      console.error('Error creating session:', error)
      // Mark session status as checked even on failure
      setSessionStatusChecked(true)
      return null
    }
  }, [
    botSlug,
    onSessionCreated,
    loadChatHistory,
    siteApiBase,
    isUserAuthenticated,
    publicUser?.id,
  ])

  // Load integration status
  const loadIntegrationStatus = useCallback(async () => {
    try {
      // Load WhatsApp status
      const whatsappData = await axiosApiData(
        `${siteApiBase}/chat/whatsapp-status`,
        { method: 'GET' },
        { whatsappEnabled: false, phoneNumber: null }
      )

      // Load Facebook status
      const facebookData = await axiosApiData(
        `${siteApiBase}/chat/facebook-status`,
        { method: 'GET' },
        { facebookEnabled: false, pageId: null }
      )

      const telegramData = await axiosApiData(
        `${siteApiBase}/chat/telegram-status`,
        { method: 'GET' },
        { telegramEnabled: false, botUsername: null }
      )

      setIntegrationStatus({
        whatsappEnabled: whatsappData?.whatsappEnabled || false,
        phoneNumber: whatsappData?.phoneNumber || null,
        facebookEnabled: facebookData?.facebookEnabled || false,
        pageId: facebookData?.pageId || null,
        telegramEnabled: telegramData?.telegramEnabled || false,
        telegramUsername: telegramData?.botUsername || null,
      })
    } catch (error) {
      console.error('Failed to load integration status:', error)
    }
  }, [siteApiBase])

  // WebSocket connection for real-time updates using centralized store
  const { sendMessage: sendWsMessage, reconnect: reconnectWebSocket } =
    useWebSocket('', {
      onConnect: () => {
        console.log('WebSocket connected for ChatCore')
      },
    })

  const {
    isConnected: wsConnected,
    isRegistered,
    connectionId,
    register,
  } = useUnifiedWebSocketConnection(true)

  useEffect(() => {
    if (!sessionId || !register) {
      return
    }

    if (!wsConnected) {
      if (wsRegistrationRetryIntervalRef.current) {
        window.clearInterval(wsRegistrationRetryIntervalRef.current)
        wsRegistrationRetryIntervalRef.current = null
      }
      return
    }

    register('user', { sessionId })

    if (isRegistered) {
      if (wsRegistrationRetryIntervalRef.current) {
        window.clearInterval(wsRegistrationRetryIntervalRef.current)
        wsRegistrationRetryIntervalRef.current = null
      }
      return
    }

    if (wsRegistrationRetryIntervalRef.current) {
      window.clearInterval(wsRegistrationRetryIntervalRef.current)
    }

    wsRegistrationRetryIntervalRef.current = window.setInterval(() => {
      register('user', { sessionId })
    }, 6000)

    return () => {
      if (wsRegistrationRetryIntervalRef.current) {
        window.clearInterval(wsRegistrationRetryIntervalRef.current)
        wsRegistrationRetryIntervalRef.current = null
      }
    }
  }, [sessionId, wsConnected, connectionId, register, isRegistered])

  useEffect(() => {
    if (sessionId !== lastWsSessionIdRef.current) {
      lastWsSessionIdRef.current = sessionId ?? null
      setWsRegistrationTimeoutReached(false)
      if (wsRegistrationTimerRef.current) {
        window.clearTimeout(wsRegistrationTimerRef.current)
        wsRegistrationTimerRef.current = null
      }
    }

    if (!sessionId) {
      if (wsRegistrationTimerRef.current) {
        window.clearTimeout(wsRegistrationTimerRef.current)
        wsRegistrationTimerRef.current = null
      }
      return
    }

    if (isRegistered) {
      if (wsRegistrationTimerRef.current) {
        window.clearTimeout(wsRegistrationTimerRef.current)
        wsRegistrationTimerRef.current = null
      }
      setWsRegistrationTimeoutReached(false)
      return
    }

    if (wsRegistrationTimeoutReached) {
      return
    }

    if (wsRegistrationTimerRef.current) {
      window.clearTimeout(wsRegistrationTimerRef.current)
      wsRegistrationTimerRef.current = null
    }

    wsRegistrationTimerRef.current = window.setTimeout(() => {
      wsRegistrationTimerRef.current = null
      setWsRegistrationTimeoutReached(true)
    }, 6000)

    return () => {
      if (wsRegistrationTimerRef.current) {
        window.clearTimeout(wsRegistrationTimerRef.current)
        wsRegistrationTimerRef.current = null
      }
    }
  }, [sessionId, isRegistered, wsRegistrationTimeoutReached])

  useEffect(() => {
    setIsRealtimeReady(isRegistered || wsRegistrationTimeoutReached)
  }, [isRegistered, wsRegistrationTimeoutReached])

  // Subscribe to WebSocket messages
  useUnifiedWebSocketMessage('connection_established', data => {
    console.log('✅ WebSocket connection established:', data)
  })

  useUnifiedWebSocketMessage('registration_confirmed', data => {
    console.log('✅ WebSocket registration confirmed:', data)
  })

  useUnifiedWebSocketMessage('session_assigned', data => {
    const eventKey = `session_assigned_${data.sessionId}_${data.agentName}_${data.timestamp}`

    if (processedMessageIds.current.has(eventKey)) {
      console.log('🔍 [DEBUG] Skipping duplicate session_assigned')
      return
    }

    processedMessageIds.current.add(eventKey)

    console.log('✅ Session assigned to agent:', data.agentName)
    setIsHandedOver(true)
    setAgentName(data.agentName || 'Agent')
    setShowHandoverButton(false)
    const assignmentMessage: Message = {
      id: generateUniqueId(),
      role: 'assistant',
      content: `🟢 You are now connected to ${data.agentName || 'an agent'}. They will assist you with your inquiry.`,
      timestamp: new Date(),
    }
    setMessages(prev => [...prev, assignmentMessage])
  })

  useUnifiedWebSocketMessage('agent_connected', data => {
    const eventKey = `agent_connected_${data.sessionId}_${data.agentName}_${data.timestamp}`

    if (processedMessageIds.current.has(eventKey)) {
      console.log('🔍 [DEBUG] Skipping duplicate agent_connected')
      return
    }

    processedMessageIds.current.add(eventKey)

    console.log('✅ Agent connected:', data.agentName)
    setIsHandedOver(true)
    setAgentName(data.agentName || 'Agent')
    setShowHandoverButton(false)
    const connectionMessage: Message = {
      id: generateUniqueId(),
      role: 'assistant',
      content: `🟢 Connected to agent: ${data.agentName || 'Agent'}`,
      timestamp: new Date(),
    }
    setMessages(prev => [...prev, connectionMessage])
  })

  useUnifiedWebSocketMessage('handover_assigned', data => {
    const eventKey = `handover_assigned_${data.sessionId}_${data.agentName}_${data.timestamp}`

    if (processedMessageIds.current.has(eventKey)) {
      console.log('🔍 [DEBUG] Skipping duplicate handover_assigned')
      return
    }

    processedMessageIds.current.add(eventKey)

    console.log('✅ Handover assigned to agent:', data.agentName)
    setIsHandedOver(true)
    setAgentName(data.agentName || 'Agent')
    setShowHandoverButton(false)

    // Show a notification to the visitor that an agent wants to chat
    const handoverMessage: Message = {
      id: generateUniqueId(),
      role: 'assistant',
      content: `🟢 ${data.agentName || 'An agent'} would like to assist you. Please start a conversation with them.`,
      timestamp: new Date(),
    }
    setMessages(prev => [...prev, handoverMessage])

    // Trigger any callback for handover assigned
    onHandoverAssigned?.()
  })

  useUnifiedWebSocketMessage('agent_message', data => {
    console.log('📥 [USER] Received agent message:', data)

    // Create a unique message identifier for deduplication
    const messageKey = `agent_message_${data.sessionId || 'unknown'}_${data.messageId}_${data.timestamp}`

    if (processedMessageIds.current.has(messageKey)) {
      console.log('🔍 [DEBUG] Skipping duplicate agent_message:', messageKey)
      return
    }

    processedMessageIds.current.add(messageKey)

    const agentMessage: Message = {
      id: data.messageId || generateUniqueId(),
      role: 'agent',
      content: data.content || '',
      timestamp: new Date(data.timestamp || Date.now()),
      agentName: data.agentName,
      imageUrl: data.imageUrl,
      imageS3Key: data.imageS3Key,
      attachments: normalizeAttachments(
        data.attachments,
        data.imageUrl,
        data.messageId || 'agent',
        data.imageS3Key
      ),
      audioUrl: data.audioUrl,
      fileUrl: data.fileUrl,
      fileName: data.fileName,
    }

    setMessages(prev => {
      // Check if message with same ID already exists
      if (prev.some(msg => msg.id === agentMessage.id)) {
        console.log(
          '🔍 [DEBUG] Agent message with same ID already exists, skipping:',
          agentMessage.id
        )
        return prev
      }
      console.log('📥 [DEBUG] Adding agent message to chat:', agentMessage)
      return [...prev, agentMessage]
    })
    onMessageReceived?.(agentMessage)
  })

  useUnifiedWebSocketMessage('session_completed', async data => {
    console.log('✅ Session completed by agent')
    setIsHandedOver(false)
    setAgentName(null)
    setShowHandoverButton(true)

    const completionMessage: Message = {
      id: generateUniqueId(),
      role: 'assistant',
      content:
        data.message ||
        '🔴 The agent has ended the session. Starting a new conversation...',
      timestamp: new Date(),
    }
    setMessages(prev => [...prev, completionMessage])
    onHandoverCompleted?.()

    console.log('🔄 Creating new session after completion...')
    setTimeout(async () => {
      localStorage.removeItem('chat_session_id')
      localStorage.removeItem('chat_session_timestamp')
      if (sessionId) {
        localStorage.removeItem(`${BOT_STORAGE_KEY_PREFIX}:${sessionId}`)
      }
      setMessages([])
      setAssignedBot(null)
      const newSessionId = await initializeSession()
      console.log('✅ New session created after completion:', newSessionId)
    }, 2000)
  })

  // Reset history loaded ref when session changes
  useEffect(() => {
    if (sessionId) {
      // If this is a different session, reset the history loaded flag
      if (historyLoadedForSession.current !== sessionId) {
        historyLoadedForSession.current = null
      }
    }
  }, [sessionId])

  // Initialize on mount - only run once
  useEffect(() => {
    initializeSession()
    loadIntegrationStatus()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Update session with userId when user logs in after session was created
  useEffect(() => {
    const updateSessionUser = async () => {
      if (sessionId && isUserAuthenticated && publicUser?.id) {
        try {
          // Use the session endpoint to update userId (upsert logic handles updates)
          await fetch(`${siteApiBase}/chat/session`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              sessionId,
              userId: publicUser.id,
            }),
          })
          console.log('✅ Updated chat session with user ID')
        } catch (error) {
          console.error('Failed to update session user:', error)
        }
      }
    }
    updateSessionUser()
  }, [sessionId, isUserAuthenticated, publicUser?.id, siteApiBase])

  // Note: Auto-scroll is handled by individual layout components (FloatingChatLayout, PageChatLayout)
  // to respect their specific UI requirements and user states

  // Send message function
  const sendMessage = async (
    text?: string,
    imageUrl?: string,
    opts?: { flowInput?: string }
  ) => {
    const imageUrlToUse = uploadedImage?.uploadError ? undefined : imageUrl

    if (!sessionId || (!text && !imageUrlToUse)) {
      return
    }

    if (uploadedImage?.isUploading) {
      console.warn('🟡 Upload still in progress, delaying send')
      return
    }

    if (!text && uploadedImage?.uploadError) {
      console.warn('❌ Cannot send image because upload failed')
      return
    }

    const messageText = text || inputText
    if (!messageText && !imageUrlToUse) {
      return
    }

    const clientMetadata = collectClientSessionMetadata()

    // Generate default content for media-only messages
    let finalMessageText = messageText
    const currentUploadedImage = uploadedImage

    // Determine media type based on file
    const isAudioFile =
      currentUploadedImage?.file?.type?.startsWith('audio/') ||
      currentUploadedImage?.file?.name?.match(/\.(mp3|wav|webm|ogg|m4a)$/i)

    const isDocumentFile =
      (currentUploadedImage?.file &&
        !isAudioFile &&
        !currentUploadedImage?.file?.type?.startsWith('image/')) ||
      currentUploadedImage?.file?.name?.match(
        /\.(pdf|doc|docx|txt|xls|xlsx|csv)$/i
      )

    // Set default message content for media-only messages
    // This is used for both backend storage and UI display
    if (!messageText && imageUrlToUse) {
      if (isAudioFile) {
        finalMessageText = 'Sent an audio message'
      } else if (isDocumentFile) {
        finalMessageText = `Sent a file: ${currentUploadedImage?.file?.name || 'document'}`
      } else {
        finalMessageText = 'Sent an image'
      }
    }

    // For audio files in UI, always show "Sent an audio message" even if there's transcription
    // The transcription is still useful for backend AI context, but UI shows simple message
    const displayMessageText =
      isAudioFile && imageUrlToUse ? 'Sent an audio message' : finalMessageText

    // Add user message to UI
    const userMessageId = generateUniqueId()

    const userMessage: Message = {
      id: userMessageId,
      role: 'user',
      content: displayMessageText,
      timestamp: new Date(),
      imageUrl: isAudioFile || isDocumentFile ? undefined : imageUrlToUse,
      imageS3Key:
        isAudioFile || isDocumentFile ? undefined : currentUploadedImage?.s3Key,
      audioUrl: isAudioFile ? imageUrlToUse : undefined,
      fileUrl: isDocumentFile ? imageUrlToUse : undefined,
      fileName: currentUploadedImage?.file?.name,
      attachments: normalizeAttachments(
        undefined,
        isAudioFile || isDocumentFile ? undefined : imageUrlToUse,
        userMessageId,
        isAudioFile || isDocumentFile ? undefined : currentUploadedImage?.s3Key
      ),
    }
    setMessages(prev => {
      const next = [...prev, userMessage]
      // If this was a menu selection, clear previous assistant menu chips to avoid stale options
      if (opts && typeof opts.flowInput !== 'undefined') {
        for (let i = next.length - 2; i >= 0; i--) {
          // -2 to skip the just-added user message
          const m = next[i] as any
          if (
            m.role === 'assistant' &&
            m.menuOptions &&
            m.menuOptions.length > 0
          ) {
            next[i] = { ...m, menuOptionsDisabled: true }
            break
          }
        }
      }
      return next
    })
    setInputText('')
    setUploadedImage(null)
    onMessageSent?.(userMessage)
    if (currentUploadedImage?.url?.startsWith('blob:')) {
      try {
        URL.revokeObjectURL(currentUploadedImage.url)
      } catch (_) {
        // ignore revoke errors
      }
    }

    const normalizedCommand = messageText.trim().toLowerCase()
    if (normalizedCommand === '/reset' && !imageUrlToUse) {
      await resetFlow()
      return
    }

    // Check if session is handed over to agent
    if (isHandedOver) {
      console.log('🚫 Session is handed over to agent, not sending to AI')
      console.log(
        '📡 [HANDOVER] Sending message via WebSocket AND saving to DB'
      )

      // Send message to agent via WebSocket
      if (wsConnected && sessionId) {
        console.log('📡 [HANDOVER] Sending user message via WebSocket:', {
          sessionId,
          content: finalMessageText,
          userId: sessionId,
        })
        const result = sendWsMessage({
          type: 'user_message',
          data: {
            sessionId,
            content: finalMessageText,
            userId: sessionId,
            messageId: userMessage.id, // Include the message ID
            timestamp: new Date().toISOString(),
            imageUrl: isAudioFile || isDocumentFile ? undefined : imageUrlToUse,
            imageS3Key:
              isAudioFile || isDocumentFile
                ? undefined
                : currentUploadedImage?.s3Key,
            audioUrl: isAudioFile ? imageUrlToUse : undefined,
            audioS3Key: isAudioFile ? currentUploadedImage?.s3Key : undefined,
            fileUrl: isDocumentFile ? imageUrlToUse : undefined,
            fileS3Key: isDocumentFile ? currentUploadedImage?.s3Key : undefined,
            fileName:
              isAudioFile || isDocumentFile
                ? currentUploadedImage?.file?.name
                : undefined,
          },
        })
        console.log('📡 [HANDOVER] WebSocket send result:', result)
      }

      // ALSO save the user message to database for chat history persistence
      try {
        // Strip query params from URLs for database storage (presigned URLs are too long)
        // Only strip when an s3Key exists; otherwise preserve the original URL so tokens aren't lost
        const baseUrlForStorage =
          currentUploadedImage?.s3Key && imageUrlToUse
            ? stripUrlQueryParams(imageUrlToUse)
            : imageUrlToUse

        const requestPayload = {
          sessionId,
          message: finalMessageText,
          model: 'gpt-4o-mini',
          handoverMode: true,
          botId: assignedBot?.id,
          metadata: clientMetadata,
          messageType: isAudioFile
            ? 'audio'
            : isDocumentFile
              ? 'document'
              : imageUrlToUse
                ? 'image'
                : 'text',
          ...(isAudioFile
            ? {
                audioUrl: baseUrlForStorage,
                audioS3Key: currentUploadedImage?.s3Key,
                fileName: currentUploadedImage?.file?.name,
              }
            : isDocumentFile
              ? {
                  fileUrl: baseUrlForStorage,
                  fileS3Key: currentUploadedImage?.s3Key,
                  fileName: currentUploadedImage?.file?.name,
                  transcription: currentUploadedImage?.transcription,
                }
              : imageUrlToUse
                ? {
                    imageUrl: baseUrlForStorage,
                    imageS3Key: currentUploadedImage?.s3Key,
                    fileName: currentUploadedImage?.file?.name,
                  }
                : {}),
        }

        await axiosApiData(`${siteApiBase}/chat/message`, {
          method: 'POST',
          data: requestPayload,
        })
      } catch (error) {
        console.error(
          '❌ [HANDOVER] Failed to save user message to database:',
          error
        )
      }

      return
    }

    setIsLoading(true)

    try {
      let flowAssignedBotId: number | null = null
      let shouldReturnAfterFlow = true
      const skipFlowForBotConversation = activeFlowBotState !== null

      // Check if flow is active OR flows are available for LLM routing
      // Always try flow execution unless handed over or in bot conversation
      // The executeFlow function will handle LLM routing internally if no default flow exists
      if (!isHandedOver && !skipFlowForBotConversation) {
        // Added safe guard: skip attempting flow execution if we clearly have no default flow
        // or the flow session / active flag isn't established yet. This prevents the initial
        // "No active flow session" error from showing as a user-visible assistant message.
        const shouldSkipFlow = !hasDefaultFlow || !flowSession || !isFlowActive

        if (shouldSkipFlow) {
          console.log(
            '[FLOW][SKIP_NO_DEFAULT] Skipping flow execution - no active or default flow',
            {
              hasDefaultFlow,
              flowSessionExists: !!flowSession,
              isFlowActive,
              messagePreview: messageText?.substring(0, 50) + '...',
            }
          )
          // Note: Don't return here - let it fall through to check if there's a bot assigned
          // If there's no bot assigned either, we'll block at the end
        } else {
          console.log('🔄 [FLOW] Processing message through flow system:', {
            isFlowActive,
            hasDefaultFlow,
            messageText: messageText?.substring(0, 50) + '...',
          })
          const flowInput =
            opts && typeof opts.flowInput !== 'undefined'
              ? opts.flowInput
              : messageText
          // Pass displayText (label) for menu selections, so it's stored in chat history
          const displayText =
            opts && typeof opts.flowInput !== 'undefined'
              ? finalMessageText
              : undefined
          const flowResult = await executeFlow(
            flowInput,
            undefined,
            displayText
          )
          console.log('🔍 [FLOW] Flow execution result:', flowResult)

          if (flowResult) {
            // Handle flow execution result
            if (flowResult.success) {
              // Always process flow result for auto-progression, even if message is empty
              // Debug: Log flow result to see what we're getting
              console.log('💬 [CHAT] Flow result received:', {
                message: flowResult.message,
                flowComplete: flowResult.flowComplete,
                shouldRestart: flowResult.shouldRestart,
                redirectFlowId: flowResult.redirectFlowId,
                hasMessage: !!(
                  flowResult.message && flowResult.message.trim() !== ''
                ),
                hasMenuOptions: !!(
                  flowResult.menuOptions && flowResult.menuOptions.length > 0
                ),
                menuOptions: flowResult.menuOptions,
                isExitAction: !!(
                  flowResult.flowComplete &&
                  !flowResult.shouldRestart &&
                  !flowResult.redirectFlowId &&
                  !flowResult.redirectFlowName &&
                  !flowResult.useNlpRedirect
                ),
              })

              // Handle form node if provided (priority over regular message)
              if (flowResult.formNode) {
                console.log(
                  '📝 [CHAT] Form node detected:',
                  flowResult.formNode
                )
                const formMessage: Message = {
                  id: generateUniqueId(),
                  role: 'assistant',
                  content: flowResult.message || '',
                  timestamp: new Date(),
                  formNode: flowResult.formNode,
                }
                console.log(
                  '📝 [CHAT] Adding form message to chat:',
                  formMessage
                )
                setMessages(prev => [...prev, formMessage])
                onMessageReceived?.(formMessage)
              } else {
                // Simplified message display logic:
                // Messages are handled via onFlowMessage callback in useFlowExecution
                // No need to add them again here to prevent duplicates
                console.log(
                  '💬 [CHAT] Flow message handled via onFlowMessage callback - skipping duplicate'
                )

                // If there are menu options, attach them to the most recent assistant message.
                // Use a microtask to ensure the onFlowMessage state update (which adds the assistant text)
                // has been applied before we try to attach the options. This prevents duplicate messages.
                if (
                  flowResult.menuOptions &&
                  flowResult.menuOptions.length > 0
                ) {
                  setTimeout(() => {
                    setMessages(prev => {
                      const updated = [...prev]

                      // Find the newest assistant message with matching content first
                      let targetIdx = -1
                      for (let i = updated.length - 1; i >= 0; i--) {
                        const m = updated[i]
                        if (m.role === 'assistant') {
                          const sameContent =
                            (m.content || '').trim() ===
                            (flowResult.message || '').trim()
                          if (sameContent) {
                            targetIdx = i
                            break
                          }
                        }
                      }

                      // If not found by content, use the last assistant message
                      if (targetIdx === -1) {
                        for (let i = updated.length - 1; i >= 0; i--) {
                          if (updated[i].role === 'assistant') {
                            targetIdx = i
                            break
                          }
                        }
                      }

                      if (targetIdx !== -1) {
                        const m = updated[targetIdx]
                        updated[targetIdx] = {
                          ...m,
                          menuOptions: flowResult.menuOptions,
                          menuOptionsDisabled: false,
                        }
                        return updated
                      }

                      // Try attaching to the most recent assistant message if text match not found
                      for (let i = updated.length - 1; i >= 0; i--) {
                        if (updated[i].role === 'assistant') {
                          targetIdx = i
                          break
                        }
                      }
                      if (targetIdx !== -1) {
                        const m2 = updated[targetIdx]
                        updated[targetIdx] = {
                          ...m2,
                          menuOptions: flowResult.menuOptions,
                          menuOptionsDisabled: false,
                        }
                        return updated
                      }

                      // As a final fallback, add a single combined message
                      const menuMsg: Message = {
                        id: generateUniqueId(),
                        role: 'assistant',
                        content: flowResult.message || '',
                        timestamp: new Date(),
                        menuOptions: flowResult.menuOptions,
                        menuOptionsDisabled: false,
                      }
                      return [...updated, menuMsg]
                    })
                  }, 0)
                }
              }

              const legacyBotConfig = (flowResult as any)?.botAssignmentConfig
              const resolvedBotAssignment =
                flowResult.botAssignment ||
                (legacyBotConfig
                  ? (() => {
                      const rawId =
                        legacyBotConfig.botId ??
                        legacyBotConfig.id ??
                        legacyBotConfig.bot_id ??
                        null
                      const parsedId =
                        typeof rawId === 'number'
                          ? rawId
                          : rawId !== null
                            ? Number(rawId)
                            : NaN
                      if (!Number.isFinite(parsedId)) {
                        return undefined
                      }
                      const transfer: 'full' | 'context' =
                        legacyBotConfig.transferType === 'context'
                          ? 'context'
                          : 'full'
                      return {
                        botId: parsedId,
                        transferType: transfer,
                      }
                    })()
                  : undefined)

              if (resolvedBotAssignment) {
                if (!flowResult.botAssignment) {
                  ;(flowResult as any).botAssignment = resolvedBotAssignment
                }
                flowAssignedBotId = resolvedBotAssignment.botId
                shouldReturnAfterFlow = false

                const waitingState =
                  (flowResult as any)?.botNodeState ||
                  legacyBotConfig?.botNodeState ||
                  null

                if (waitingState?.waitingForEndTool) {
                  const botState = {
                    nodeId:
                      waitingState.nodeId ||
                      String(resolvedBotAssignment.botId),
                    botId: resolvedBotAssignment.botId,
                  }
                  activeFlowBotStateRef.current = botState
                  setActiveFlowBotState(botState)
                } else {
                  activeFlowBotStateRef.current = null
                  setActiveFlowBotState(null)
                }
              } else {
                activeFlowBotStateRef.current = null
                setActiveFlowBotState(null)
              }

              // ALWAYS return when flow processes successfully to prevent normal chat processing
              if (shouldReturnAfterFlow) {
                // Only continue to bot processing if flow explicitly expects bot assignment
                const expectsBotAssignment =
                  Boolean(
                    (flowResult as any)?.botNodeState?.waitingForEndTool
                  ) || flowResult.nextNodeType === 'bot_assignment'

                if (expectsBotAssignment) {
                  console.log(
                    '🕓 [CHAT] Waiting for bot assignment to finish before proceeding'
                  )
                  await waitForBotAssignment()
                  const resolvedAfterWait =
                    activeFlowBotStateRef.current?.botId ??
                    assignedBotRef.current?.id ??
                    null

                  if (resolvedAfterWait) {
                    flowAssignedBotId = resolvedAfterWait
                    shouldReturnAfterFlow = false
                    console.log(
                      '✅ [CHAT] Bot assignment ready after wait:',
                      resolvedAfterWait
                    )
                  } else {
                    console.warn(
                      '⚠️ [CHAT] Bot assignment wait finished without resolving a bot'
                    )
                  }
                }

                if (!shouldReturnAfterFlow) {
                  console.log(
                    '🤖 [CHAT] Flow delegation completed after wait; continuing with bot conversation handling'
                  )
                  // fall through to normal bot handling
                } else {
                  console.log(
                    '🚫 [CHAT] Flow processed successfully - preventing normal chat response'
                  )
                  setIsLoading(false)
                  return
                }
              }
              if (!shouldReturnAfterFlow) {
                console.log(
                  '🤖 [CHAT] Flow delegated to bot; continuing with bot conversation handling'
                )
              }
            } else if (flowResult.error) {
              const loweredError = String(flowResult.error).toLowerCase()
              // Suppress the specific expected missing session error; silently fall back
              if (loweredError.includes('no active flow session')) {
                console.log(
                  '[FLOW][EXPECTED_NO_SESSION] Suppressing missing flow session error; falling back to normal chat'
                )
              } else if (flowResult.shouldRetry) {
                console.log(
                  '🔄 [CHAT] Flow routing failed - showing retry message'
                )
                const retryMessage: Message = {
                  id: generateUniqueId(),
                  role: 'assistant',
                  content: flowResult.error,
                  timestamp: new Date(),
                }
                setMessages(prev => [...prev, retryMessage])
                setIsLoading(false)
                return
              } else {
                // Handle other flow errors - show error message to user
                console.error('Flow execution error:', flowResult)
                if (flowResult.error) {
                  console.error('Flow execution detail:', flowResult.error)
                }
                const errorMessage: Message = {
                  id: generateUniqueId(),
                  role: 'assistant',
                  content: getFlowErrorDisplayMessage(flowResult.error),
                  timestamp: new Date(),
                }
                setMessages(prev => [...prev, errorMessage])
                // Continue to normal chat processing as fallback
              }
            }
          }
        }
      } else if (skipFlowForBotConversation) {
        flowAssignedBotId = activeFlowBotState?.botId ?? assignedBot?.id ?? null
        shouldReturnAfterFlow = false
      }

      // Check if we should block normal chat when no flow
      const resolvedBotId = flowAssignedBotId ?? assignedBot?.id ?? null
      const shouldBlockNormalChat =
        !hasDefaultFlow && // No default flow exists
        !flowSession && // No active flow session
        !isFlowActive // Flow is not active

      if (shouldBlockNormalChat) {
        console.log('🚫 [CHAT] Blocking message - no active or default flow', {
          hasDefaultFlow,
          flowSession: !!flowSession,
          isFlowActive,
        })
        setIsLoading(false)
        return
      }

      // Default to normal chat processing if no active flow or flow processing failed
      let responseData

      if (imageUrlToUse && !isAudioFile && !isDocumentFile) {
        // Send image analysis request to Next.js API route
        console.log('📤 [NORMAL] Sending image analysis to Next.js API:', {
          sessionId,
          imageUrl: imageUrlToUse,
          prompt: finalMessageText || t('imagePrompt'),
        })
        responseData = await axiosApiData(`/api/chat/image`, {
          method: 'POST',
          data: {
            sessionId,
            imageUrl: imageUrlToUse,
            prompt: finalMessageText || t('imagePrompt'),
            botId: resolvedBotId,
            metadata: clientMetadata,
            messageType: 'image',
            fileName: currentUploadedImage?.file?.name,
            ...(currentUploadedImage?.s3Key
              ? { imageS3Key: currentUploadedImage.s3Key }
              : {}),
          },
        })
      } else {
        // Send text message to Next.js API route
        const requestData = {
          sessionId,
          message: finalMessageText,
          model: 'gpt-4o-mini', // Default model
          botId: resolvedBotId,
          metadata: clientMetadata,
          messageType: isAudioFile
            ? 'audio'
            : isDocumentFile
              ? 'document'
              : imageUrlToUse
                ? 'image'
                : 'text',
          ...(isAudioFile
            ? {
                audioUrl: imageUrlToUse,
                audioS3Key: currentUploadedImage?.s3Key,
                fileName: currentUploadedImage?.file?.name,
                transcription: currentUploadedImage?.transcription,
              }
            : isDocumentFile
              ? {
                  fileUrl: imageUrlToUse,
                  fileS3Key: currentUploadedImage?.s3Key,
                  fileName: currentUploadedImage?.file?.name,
                }
              : imageUrlToUse
                ? {
                    imageUrl: imageUrlToUse,
                    imageS3Key: currentUploadedImage?.s3Key,
                    fileName: currentUploadedImage?.file?.name,
                  }
                : {}),
        }
        console.log('📤 [NORMAL] Sending message to Next.js API:', requestData)
        responseData = await axiosApiData(`/api/chat/message`, {
          method: 'POST',
          data: requestData,
        })
        console.log(
          '✅ [NORMAL] API response status:',
          responseData?.success !== false ? 'success' : 'failed'
        )
        if (responseData?.success === false || responseData?.error) {
          console.error('❌ [NORMAL] API response error:', {
            success: responseData?.success,
            error: responseData?.error,
            message: responseData?.message,
          })
        }
      }

      // Add assistant response to UI
      const assistantMessageId = generateUniqueId()
      const assistantMessage: Message = {
        id: assistantMessageId,
        role: 'assistant',
        content:
          responseData?.message ||
          responseData?.answer ||
          'No response received',
        timestamp: new Date(),
        sources: responseData?.sources || undefined,
        imageUrl: responseData?.imageUrl,
        imageS3Key: responseData?.imageS3Key,
        attachments: normalizeAttachments(
          responseData?.attachments,
          responseData?.imageUrl,
          assistantMessageId,
          responseData?.imageS3Key
        ),
      }
      const trimmedAssistantContent = (assistantMessage.content || '').trim()
      let assistantAdded = false
      let assistantSnapshot: Message[] | null = null

      setMessages(prev => {
        const last = prev[prev.length - 1]
        if (
          last &&
          last.role === 'assistant' &&
          (last.content || '').trim() === trimmedAssistantContent
        ) {
          console.log(
            '🛑 [CHAT CORE] Skipping duplicate assistant message from normal response:',
            trimmedAssistantContent
          )
          return prev
        }
        const updated = [...prev, assistantMessage]
        assistantSnapshot = updated
        assistantAdded = true
        return updated
      })
      if (assistantAdded && assistantSnapshot) {
        console.log('🎯 [CHAT CORE] Added assistant reply:', assistantMessage)
        onMessageReceived?.(assistantMessage)
      }

      if (responseData?.flowContinuation?.clearAssignedBot) {
        setAssignedBot(null)
        persistAssignedBot(null, sessionId)
        assignedBotRef.current = null
        setActiveFlowBotState(null)
        activeFlowBotStateRef.current = null
      }

      if (responseData?.flowContinuation?.resumeFlow) {
        activeFlowBotStateRef.current = null
        setActiveFlowBotState(null)
        setTimeout(() => {
          executeFlow('')
        }, 0)
      }
    } catch (error) {
      console.error('Failed to send message:', error)
      // Add error message
      const errorMessage: Message = {
        id: generateUniqueId(),
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date(),
      }
      setMessages(prev => [...prev, errorMessage])
      onMessageReceived?.(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  // Request handover to human agent
  const requestHandover = async (handoverConfig?: FlowHandoverConfig) => {
    if (!sessionId) {
      return
    }

    // Check if session is already handed over to prevent duplicate requests
    if (isHandedOver) {
      console.log(
        '🔄 [CHAT CORE] Session is already handed over, skipping handover request'
      )
      return
    }

    // If this request came from a flow agent_handover node, the server has already
    // created the handover request and flagged the session as handed over.
    // Avoid calling the REST API again to prevent a 400 "already handed over".
    if (handoverConfig) {
      console.log('🤖 [CHAT CORE] Flow-driven handover: skipping REST call')
      setShowHandoverButton(false)
      // Optimistically mark as handed over; server state was already updated by the flow
      setIsHandedOver(true)
      return
    }

    // Cast to a known type to satisfy TypeScript narrowing in case inference fails
    const cfg = handoverConfig as FlowHandoverConfig | undefined

    const priority = cfg?.priority ?? 'normal'
    const reason = cfg?.message || 'User requested human assistance'
    const teamId =
      typeof cfg?.teamId === 'number' && Number.isFinite(cfg.teamId)
        ? cfg.teamId
        : null
    const teamName = typeof cfg?.teamName === 'string' ? cfg.teamName : null

    try {
      const response = await api.chat.requestHandover({
        sessionId,
        reason,
        priority,
        teamId,
        teamName,
        specificAgentId: cfg?.specificAgentId,
        sendToAllAgents: cfg?.sendToAllAgents,
        autoAssign: cfg?.autoAssign,
      })

      if (response.success) {
        setShowHandoverButton(false)

        const waitingMessage =
          cfg?.message ||
          "I've requested a human agent to assist you. Please wait while we connect you to an available agent."

        const systemMessage: Message = {
          id: generateUniqueId(),
          role: 'assistant',
          content: waitingMessage,
          timestamp: new Date(),
        }
        setMessages(prev => [...prev, systemMessage])
        onHandoverRequested?.()

        if (wsConnected) {
          sendWsMessage({
            type: 'handover_requested',
            data: {
              sessionId,
              reason,
              priority,
              teamId,
              teamName,
              specificAgentId: cfg?.specificAgentId,
            },
          })
        }
      } else if (response.error) {
        console.error('Failed to request handover:', response.error)

        // If the error is because session is already handed over, set the state and stop retrying
        if (response.error.includes('already handed over')) {
          console.log(
            '🔄 [CHAT CORE] Session already handed over, updating state'
          )
          setIsHandedOver(true)
          setShowHandoverButton(false)
          return // Don't show error message, just stop retrying
        }

        const errorMessage: Message = {
          id: generateUniqueId(),
          role: 'assistant',
          content:
            'We could not connect you to an agent right now. Please try again shortly.',
          timestamp: new Date(),
        }
        setMessages(prev => [...prev, errorMessage])
      }
    } catch (error) {
      console.error('Failed to request handover:', error)

      // If the error is because session is already handed over, set the state and stop retrying
      const errorMessage =
        error instanceof Error ? error.message : String(error)
      if (errorMessage.includes('already handed over')) {
        console.log(
          '🔄 [CHAT CORE] Session already handed over (caught), updating state'
        )
        setIsHandedOver(true)
        setShowHandoverButton(false)
        setIsLoading(false)
        return // Don't show error message, just stop retrying
      }

      const errorResponse: Message = {
        id: generateUniqueId(),
        role: 'assistant',
        content:
          'We encountered an issue while connecting you to an agent. Please try again.',
        timestamp: new Date(),
      }
      setMessages(prev => [...prev, errorResponse])
    }
  }

  // File upload handling
  const onDrop = async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0]
    if (!file) {
      return
    }

    try {
      const previewUrl = URL.createObjectURL(file)
      setUploadedImage({ url: previewUrl, file, isUploading: true })

      const formData = new FormData()
      formData.append('file', file)

      const response = await api.upload.uploadFile(formData)

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Upload failed')
      }

      const uploadData = response.data

      const resolvedUrl =
        typeof uploadData.presignedUrl === 'string' &&
        uploadData.presignedUrl.trim().length > 0
          ? uploadData.presignedUrl.trim()
          : uploadData.url

      // Handle all file types: image, audio, document
      if (resolvedUrl) {
        URL.revokeObjectURL(previewUrl)
        setUploadedImage({
          url: resolvedUrl,
          file,
          isUploading: false,
          s3Key: uploadData.s3Key,
        })
      } else {
        setUploadedImage(prev =>
          prev && prev.file === file
            ? {
                ...prev,
                isUploading: false,
                uploadError: 'Upload failed - no URL returned',
              }
            : prev
        )
      }
    } catch (error) {
      console.error('Failed to upload file:', error)
      setUploadedImage(prev => {
        if (!prev) return null
        if (prev.file !== file) return prev
        if (prev.url.startsWith('blob:')) {
          try {
            URL.revokeObjectURL(prev.url)
          } catch (_) {
            // ignore revoke errors
          }
        }
        return { ...prev, isUploading: false, uploadError: 'Upload failed' }
      })
    }
  }

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp'],
      'audio/*': ['.mp3', '.wav', '.webm', '.ogg'],
    },
    maxFiles: 1,
    noClick: true, // Disable click-to-open file dialog
  })

  // Clear uploaded image
  const clearUploadedImage = () => {
    setUploadedImage(prev => {
      if (prev?.url?.startsWith('blob:')) {
        try {
          URL.revokeObjectURL(prev.url)
        } catch (_) {
          // ignore revoke errors
        }
      }
      return null
    })
  }

  const showWsConnectionIssue = wsRegistrationTimeoutReached && !isRegistered
  const wsConnectionMessage = showWsConnectionIssue
    ? 'Real-time updates are temporarily unavailable. We are retrying the live connection.'
    : null
  const wsReconnecting = !wsConnected || !isRegistered
  const handleManualReconnect = useCallback(() => {
    reconnectWebSocket()
    if (wsRegistrationRetryIntervalRef.current) {
      window.clearInterval(wsRegistrationRetryIntervalRef.current)
      wsRegistrationRetryIntervalRef.current = null
    }
    if (!sessionId) {
      return
    }
    window.setTimeout(() => {
      register('user', { sessionId })
    }, 500)
  }, [reconnectWebSocket, register, sessionId])

  // Render props pattern
  const renderProps: ChatCoreRenderProps = {
    // State
    messages,
    inputText,
    isLoading,
    sessionId,
    uploadedImage,
    integrationStatus,
    isHandedOver,
    agentName,
    showHandoverButton,
    hasNewMessage,
    wsConnected,
    wsConnectionIssue: showWsConnectionIssue,
    wsConnectionMessage,
    wsReconnecting,
    retryWebSocket: handleManualReconnect,

    // Flow state
    isFlowActive,
    flowSession,
    isProcessingFlow,
    flowError,
    hasDefaultFlow,
    defaultBot,

    // Voice recording
    isRecording,
    isProcessingVoice,
    voiceError,
    isVoiceSupported,

    // Drag and drop
    isDragActive,
    getRootProps,
    getInputProps,

    // Actions
    setInputText,
    sendMessage,
    clearUploadedImage,
    requestHandover,
    toggleRecording,
    clearVoiceError,
    resetFlow,
    clearFlowError,
    onDrop,

    // Expose flow executor for form/menu follow-ups
    executeFlow,

    // Refs
    messagesContainerRef,
    inputRef,

    // Utilities
    t,
  }

  return (
    <div className={className}>
      {children(renderProps)}
      <FlowDebugger
        sessionId={sessionId}
        isFlowActive={isFlowActive}
        flowSession={flowSession}
        hasDefaultFlow={hasDefaultFlow}
        flowError={flowError}
        initializeFlow={initFlow}
        isHandedOver={isHandedOver}
        assignedBotId={assignedBot?.id}
        activeFlowBotId={activeFlowBotState?.botId}
      />
    </div>
  )
}
